import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, PhoneCall, Mail, MessageSquare, CheckCircle2, XCircle, Timer, Settings } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { lrInbox, lrStats, lrLeadTimeline, lrSettings, lrSettingsPatch } from "@/lib/api";

export default function LeadResponseInbox() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    setError("");
    try {
      const [i, s, cfg] = await Promise.all([lrInbox(), lrStats(), lrSettings()]);
      setItems(i.items || []); setStats(s); setSettings(cfg);
    } catch (e) {
      setError(e?.response?.data?.detail || "Sign in required to view the lead inbox.");
    }
  };
  useEffect(() => { document.title = "Lead Response Inbox"; refresh(); }, []);
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    lrLeadTimeline(selected).then(setDetail).catch(() => setDetail(null));
  }, [selected]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="lr-inbox">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/ai-lead-response" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> AI Lead Response</Link>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Lead Response Inbox</h1>
            <button onClick={() => setShowSettings((s) => !s)} data-testid="lr-settings-toggle"
              className="inline-flex items-center gap-2 text-sm rounded-lg border border-slate-300 bg-white px-3 py-2 font-semibold hover:border-indigo-400">
              <Settings className="w-4 h-4" /> Settings
            </button>
          </div>
          <p className="mt-2 text-slate-600">Every website inquiry, response time, and follow-up in one place.</p>

          {error && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{error}</div>}

          {/* Stats */}
          {stats && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard label="Leads received (30d)" value={stats.leads_received} tone="indigo" testId="lr-stat-received" />
              <StatCard label="Response rate" value={`${stats.response_rate_pct}%`} tone="emerald" testId="lr-stat-rr" />
              <StatCard label={`Within ${stats.sla_seconds}s SLA`} value={`${stats.within_sla_pct}%`} tone="amber" testId="lr-stat-sla" />
              <StatCard label="TTFR p50" value={stats.ttfr_seconds_p50 !== null ? `${stats.ttfr_seconds_p50}s` : "—"} tone="slate" testId="lr-stat-p50" />
              <StatCard label="TTFR p95" value={stats.ttfr_seconds_p95 !== null ? `${stats.ttfr_seconds_p95}s` : "—"} tone="slate" testId="lr-stat-p95" />
            </div>
          )}

          {/* Settings panel */}
          {showSettings && settings && (
            <SettingsPanel settings={settings} onSave={async (patch) => {
              const upd = await lrSettingsPatch(patch);
              setSettings(upd); refresh();
            }} />
          )}

          {/* Inbox + detail */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <section className="md:col-span-1 rounded-2xl bg-white border border-slate-200 p-3" data-testid="lr-inbox-list">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold p-2">Leads</div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {items.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No leads yet.</div>}
                {items.map((l) => {
                  const sla = l.lr?.sla_met;
                  return (
                    <button key={l.id} onClick={() => setSelected(l.id)} data-testid={`lr-inbox-item-${l.id}`}
                      className={`w-full text-left rounded-lg border p-3 transition ${selected === l.id ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-indigo-300"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {l.requested_service || l.source || "Lead"}
                        </div>
                        {sla === true && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                        {sla === false && <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
                        {sla === null && <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500 uppercase tracking-widest">{l.stage} · {l.source}</div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {l.lr?.ttfr_seconds !== null && l.lr?.ttfr_seconds !== undefined
                          ? <>TTFR <strong>{Math.round(l.lr.ttfr_seconds)}s</strong></>
                          : "Not yet contacted"} · {l.lr?.attempts_count || 0} attempt(s)
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="md:col-span-2 rounded-2xl bg-white border border-slate-200 p-5" data-testid="lr-inbox-detail">
              {!detail ? (
                <div className="text-center text-sm text-slate-500 py-16">Select a lead to see its response timeline.</div>
              ) : (
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Lead {detail.lead_id.slice(0, 8)}</div>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <MetricCell label="TTFR" value={detail.metrics.ttfr_seconds !== null ? `${Math.round(detail.metrics.ttfr_seconds * 1000)/1000}s` : "—"} />
                    <MetricCell label="TTFC" value={detail.metrics.ttfc_seconds !== null ? `${Math.round(detail.metrics.ttfc_seconds)}s` : "—"} />
                    <MetricCell label="Events" value={detail.events.length} />
                    <MetricCell label="Attempts" value={detail.attempts.length} />
                  </div>

                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Timeline</div>
                    <div className="mt-3 space-y-2">
                      {detail.events.map((e) => (
                        <div key={e.id} className="flex gap-3 items-start rounded-lg border border-slate-100 bg-slate-50 p-2" data-testid={`lr-event-${e.kind}`}>
                          <Timer className="w-4 h-4 text-indigo-600 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-slate-900">{e.kind.replaceAll("_", " ")}</div>
                            <div className="text-[11px] text-slate-500">{new Date(e.at).toLocaleString()}</div>
                            {e.payload && Object.keys(e.payload).length > 0 && (
                              <div className="mt-1 text-[11px] text-slate-600 font-mono">{JSON.stringify(e.payload)}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Attempts</div>
                    <div className="mt-3 space-y-2">
                      {detail.attempts.map((a) => {
                        const Icon = a.channel === "voice" ? PhoneCall : a.channel === "email" ? Mail : MessageSquare;
                        const tone = a.status === "queued" ? "text-indigo-700 bg-indigo-50 border-indigo-200"
                                       : a.status === "delivered" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                                       : a.status === "skipped_ineligible" ? "text-amber-700 bg-amber-50 border-amber-200"
                                       : "text-rose-700 bg-rose-50 border-rose-200";
                        return (
                          <div key={a.id} className={`flex items-center gap-3 rounded-lg border p-2 text-sm ${tone}`} data-testid={`lr-attempt-${a.id}`}>
                            <Icon className="w-4 h-4" />
                            <div className="flex-1">
                              <div className="font-semibold capitalize">{a.channel} · {a.status.replaceAll("_", " ")}</div>
                              {a.template && <div className="text-[11px] opacity-75">Template: {a.template}</div>}
                              {a.error && <div className="text-[11px] opacity-75">Reason: {a.error}</div>}
                            </div>
                            <div className="text-[11px] opacity-75">{new Date(a.at).toLocaleTimeString()}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function SettingsPanel({ settings, onSave }) {
  const [sla, setSla] = useState(settings.sla_seconds);
  const [maxAttempts, setMaxAttempts] = useState(settings.max_attempts);
  const [qhStart, setQhStart] = useState(settings.quiet_hours?.start || "21:00");
  const [qhEnd, setQhEnd] = useState(settings.quiet_hours?.end || "08:00");
  const [cwStart, setCwStart] = useState(settings.calling_window?.start || "09:00");
  const [cwEnd, setCwEnd] = useState(settings.calling_window?.end || "20:00");
  const [active, setActive] = useState(settings.active);

  const save = () => onSave({
    sla_seconds: Number(sla), max_attempts: Number(maxAttempts),
    quiet_hours: { start: qhStart, end: qhEnd },
    calling_window: { start: cwStart, end: cwEnd },
    active,
  });

  return (
    <div className="mt-6 rounded-2xl bg-white border border-indigo-200 p-5" data-testid="lr-settings-panel">
      <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">Speed-to-Lead settings</div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <SetField label="SLA (seconds)"><input type="number" value={sla} onChange={(e) => setSla(e.target.value)} data-testid="lr-set-sla" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></SetField>
        <SetField label="Max attempts"><input type="number" value={maxAttempts} onChange={(e) => setMaxAttempts(e.target.value)} data-testid="lr-set-max" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></SetField>
        <SetField label="Orchestrator active">
          <div className="flex items-center gap-2 py-2">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="lr-set-active" className="w-4 h-4 accent-indigo-600" />
            <span className="text-sm">{active ? "On" : "Paused"}</span>
          </div>
        </SetField>
        <SetField label="Quiet hours (SMS blocked)"><div className="flex gap-2"><input type="time" value={qhStart} onChange={(e) => setQhStart(e.target.value)} data-testid="lr-set-qh-start" className="rounded-lg border border-slate-300 px-2 py-2 text-sm" /><input type="time" value={qhEnd} onChange={(e) => setQhEnd(e.target.value)} data-testid="lr-set-qh-end" className="rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div></SetField>
        <SetField label="Calling window (voice)"><div className="flex gap-2"><input type="time" value={cwStart} onChange={(e) => setCwStart(e.target.value)} data-testid="lr-set-cw-start" className="rounded-lg border border-slate-300 px-2 py-2 text-sm" /><input type="time" value={cwEnd} onChange={(e) => setCwEnd(e.target.value)} data-testid="lr-set-cw-end" className="rounded-lg border border-slate-300 px-2 py-2 text-sm" /></div></SetField>
      </div>
      <div className="mt-4">
        <button onClick={save} data-testid="lr-set-save" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 px-5 py-2.5 text-white font-semibold text-sm transition">Save settings</button>
      </div>
    </div>
  );
}
function SetField({ label, children }) {
  return (<label className="block"><div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>);
}
function MetricCell({ label, value }) {
  return (<div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{label}</div><div className="mt-1 text-lg font-black text-slate-900">{value}</div></div>);
}
function StatCard({ label, value, tone, testId }) {
  const bg = { indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
                emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
                amber: "bg-amber-50 text-amber-800 border-amber-200",
                slate: "bg-slate-100 text-slate-700 border-slate-200" }[tone];
  return (
    <div className={`rounded-xl border p-4 ${bg}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-widest font-bold opacity-75">{label}</div>
      <div className="mt-1 text-2xl font-black">{value}</div>
    </div>
  );
}
